import { useMemo } from 'react'
import { project3DPointToCamera } from '@utils/cameraProjection'

interface ChatQuestionMarkerProps {
  /** Frame index where the customer is / asked the question */
  questionFrameIndex: number
  /** Image pixel X of the question location (optional for live tracking) */
  imagePixelX?: number
  /** Image pixel Y of the question location (optional for live tracking) */
  imagePixelY?: number
  /** 3D point for cross-frame projection */
  point3D?: [number, number, number] | null
  /** Current frame index being rendered */
  frameIndex: number
  /** Camera data for the current frame */
  frameCamera: any
  /** Current frame display width */
  frameDisplayWidth: number
  /** Current frame display height */
  frameDisplayHeight: number
  /** Whether the customer is live (online and moving) */
  isLive?: boolean
}

/**
 * ChatQuestionMarker — Shows a pulsing chat-bubble marker on a frame
 * to indicate where a customer's question refers to.
 *
 * Used when the shop owner opens a push-notification deep-link.
 * Supports cross-frame 3D projection so the marker follows
 * the point across different camera views.
 */
export function ChatQuestionMarker({
  questionFrameIndex,
  imagePixelX,
  imagePixelY,
  point3D,
  frameIndex,
  frameCamera,
  frameDisplayWidth,
  frameDisplayHeight,
  isLive = false,
}: ChatQuestionMarkerProps) {
  const position = useMemo(() => {
    const imageWidth = frameCamera.intrinsics.width
    const imageHeight = frameCamera.intrinsics.height
    const scaleX = frameDisplayWidth / imageWidth
    const scaleY = frameDisplayHeight / imageHeight

    let imgX: number | null = null
    let imgY: number | null = null

    // Exact frame match — use stored pixel coords or center
    if (questionFrameIndex === frameIndex) {
      imgX = imagePixelX ?? imageWidth / 2
      imgY = imagePixelY ?? imageHeight / 2
    }
    // 3D projection to other frames
    else if (point3D) {
      try {
        const projected = project3DPointToCamera(point3D, frameCamera)
        if (projected) {
          const [px, py] = projected
          if (px >= -50 && px <= imageWidth + 50 && py >= -50 && py <= imageHeight + 50) {
            imgX = px
            imgY = py
          }
        }
      } catch {
        // projection failure — skip
      }
    }

    if (imgX === null || imgY === null) return null

    return {
      displayX: imgX * scaleX,
      displayY: imgY * scaleY,
    }
  }, [questionFrameIndex, imagePixelX, imagePixelY, point3D, frameIndex, frameCamera, frameDisplayWidth, frameDisplayHeight])

  if (!position) return null

  const color = isLive ? '#16a34a' : '#2563eb'

  return (
    <>
      {/* Inject pulse keyframes once */}
      <style>{`
        @keyframes chatMarkerPulse {
          0%, 100% { transform: translate(-50%, -100%) scale(1); }
          50% { transform: translate(-50%, -100%) scale(1.15); }
        }
        @keyframes chatMarkerRing {
          0% { transform: translate(-50%, -50%) scale(1); opacity: 0.6; }
          100% { transform: translate(-50%, -50%) scale(2.5); opacity: 0; }
        }
      `}</style>

      {/* Expanding ring at the base point */}
      <div
        style={{
          position: 'absolute',
          left: position.displayX,
          top: position.displayY,
          width: 16,
          height: 16,
          borderRadius: '50%',
          border: `2px solid ${color}`,
          pointerEvents: 'none',
          zIndex: 10,
          animation: 'chatMarkerRing 2s ease-out infinite',
        }}
      />

      {/* Chat bubble + pointer */}
      <div
        style={{
          position: 'absolute',
          left: position.displayX,
          top: position.displayY,
          zIndex: 11,
          pointerEvents: 'none',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          animation: 'chatMarkerPulse 2s ease-in-out infinite',
        }}
      >
        {/* Bubble */}
        <div
          style={{
            background: color,
            color: 'white',
            borderRadius: '12px 12px 12px 2px',
            padding: '5px 8px',
            fontSize: isLive ? '11px' : '16px',
            fontWeight: isLive ? 700 : 400,
            lineHeight: 1,
            boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            gap: 3,
          }}
        >
          {isLive && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff', display: 'inline-block' }} />}
          {isLive ? 'LIVE' : '💬'}
        </div>
        {/* Pointer triangle */}
        <div
          style={{
            width: 0,
            height: 0,
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderTop: `6px solid ${color}`,
            marginTop: -1,
          }}
        />
      </div>
    </>
  )
}
